import { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';

const Hero = memo(() => {
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  // Freeze PepCoin price at $0.003 until after pre-launch
  const pepPrice = 0.003;

  const advertisements = [
    {
      title: "🎉 Welcome Bonus: 1,000 PEP",
      description: "Get 1,000 PepCoin instantly when you sign up today",
      cta: "Claim Now",
      link: "/auth/signup",
      gradient: "from-green-500 to-emerald-600"
    },
    {
      title: "⚡ Premium Trading Features",
      description: "Access advanced AI trading tools and analytics",
      cta: "Upgrade Now",
      link: "/trading",
      gradient: "from-blue-500 to-cyan-600"
    },
    {
      title: "🎮 PepVerse Gaming Launch",
      description: "Play games and earn PepCoin rewards",
      cta: "Play Now",
      link: "/pepverse",
      gradient: "from-indigo-500 to-purple-600"
    },
    {
      title: "🔄 P2P Trading Available",
      description: "Trade directly with other users - secure and instant",
      cta: "Start P2P",
      link: "/p2p",
      gradient: "from-teal-500 to-green-600"
    },
    {
      title: "💰 P2P Arbitrage Profits",
      description: "Earn from price differences across exchanges",
      cta: "Trade Now",
      link: "/p2p",
      gradient: "from-orange-500 to-red-600"
    }
  ];

  // Auto-scroll ads
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAdIndex((prevIndex) => 
        prevIndex === advertisements.length - 1 ? 0 : prevIndex + 1
      );
    }, 15000);

    return () => clearInterval(interval);
  }, [advertisements.length]);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-800 via-blue-900 to-indigo-900 overflow-hidden">
      {/* Get PepCoin Before Everyone Banner */}
      <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 py-2 px-3 sm:py-2.5 sm:px-4 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-white/20 backdrop-blur-sm px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border border-white/30">
                <span className="text-white font-bold text-[10px] sm:text-xs">🚀 LIVE NOW</span>
              </div>
              <p className="text-white font-semibold text-xs sm:text-sm text-center sm:text-left">
                Get PepCoin Before Everyone - Limited Time Offer!
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex gap-1">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full animate-pulse"></div>
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white/70 rounded-full animate-pulse delay-75"></div>
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white/50 rounded-full animate-pulse delay-150"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Advertisement Banner */}
      <div className="relative z-20 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 py-2 sm:py-2.5 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center text-center">
            <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
              <div className="flex items-center justify-center space-x-2 sm:space-x-4">
                {/* PepCoin Price Display */}
                <div className="flex items-center space-x-1.5 bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1 border border-white/30">
                  <img 
                    src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
                    alt="PepCoin" 
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                    loading="eager"
                  />
                  <span className="text-white font-bold text-xs">
                    PEP $0.003
                  </span>
                  <span className="text-blue-200 text-[10px] font-medium">
                    Pre-Launch
                  </span>
                </div>
                
                <span className="text-white font-bold text-xs sm:text-sm">
                  {advertisements[currentAdIndex].title}
                </span>
                <span className="hidden sm:inline text-white/90 text-xs">
                  {advertisements[currentAdIndex].description}
                </span>
                <Link 
                  to={advertisements[currentAdIndex].link}
                  className="bg-white text-orange-600 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold hover:bg-orange-50 transition-colors whitespace-nowrap shadow-lg cursor-pointer"
                >
                  {advertisements[currentAdIndex].cta}
                </Link>
              </div>
            </div>
          </div>
          
          {/* Advertisement indicators */}
          <div className="flex justify-center mt-1.5 space-x-1.5">
            {advertisements.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentAdIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-colors cursor-pointer ${
                  index === currentAdIndex ? 'bg-white' : 'bg-white/50'
                }`}
                aria-label={`Go to ad ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Background Elements - Small decorative icons only */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Bitcoin Logo - Small */}
        <div className="absolute top-20 left-10 opacity-10 animate-pulse">
          <img 
            src="https://public.readdy.ai/ai/img_res/bc9be9e9a36e71fb7f2e1e437f92ee9d.jpg" 
            alt="" 
            className="w-16 h-16"
          />
        </div>
        
        {/* Ethereum Logo - Small */}
        <div className="absolute top-32 right-16 opacity-8 animate-bounce">
          <img 
            src="https://readdy.ai/api/search-image?query=Ethereum%20cryptocurrency%20logo%20purple%20diamond%20shaped%20crystal%20ETH%20symbol%20on%20transparent%20background%20blockchain%20digital%20currency&width=48&height=48&seq=eth1&orientation=squarish" 
            alt="" 
            className="w-12 h-12"
          />
        </div>

        {/* Small PepCoin - Background decoration only */}
        <div className="absolute top-60 left-1/4 opacity-6 animate-pulse delay-1000">
          <img 
            src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
            alt="" 
            className="w-20 h-20"
          />
        </div>

        {/* Tether USDT Logo - Small */}
        <div className="absolute bottom-40 right-20 opacity-8 animate-pulse delay-500">
          <img 
            src="https://readdy.ai/api/search-image?query=Tether%20USDT%20cryptocurrency%20logo%20green%20circular%20coin%20with%20T%20symbol%20stablecoin%20digital%20currency%20icon%20on%20transparent%20background&width=48&height=48&seq=usdt1&orientation=squarish" 
            alt="" 
            className="w-12 h-12"
          />
        </div>

        {/* Cardano ADA Logo - Small */}
        <div className="absolute bottom-60 left-20 opacity-6 animate-bounce delay-700">
          <img 
            src="https://readdy.ai/api/search-image?query=Cardano%20ADA%20cryptocurrency%20logo%20blue%20circular%20coin%20with%20geometric%20symbol%20blockchain%20digital%20currency%20icon%20on%20transparent%20background&width=56&height=56&seq=ada1&orientation=squarish" 
            alt="" 
            className="w-14 h-14"
          />
        </div>

        {/* Polkadot DOT Logo - Small */}
        <div className="absolute bottom-20 left-1/3 opacity-8 animate-bounce delay-300">
          <img 
            src="https://readdy.ai/api/search-image?query=Polkadot%20DOT%20cryptocurrency%20logo%20pink%20circular%20coin%20with%20dots%20pattern%20blockchain%20digital%20currency%20icon%20on%20transparent%20background&width=48&height=48&seq=dot1&orientation=squarish" 
            alt="" 
            className="w-12 h-12"
          />
        </div>

        {/* Binance Coin BNB Logo - Small */}
        <div className="absolute top-40 right-1/3 opacity-6 animate-pulse delay-2000">
          <img 
            src="https://readdy.ai/api/search-image?query=Binance%20Coin%20BNB%20cryptocurrency%20logo%20yellow%20circular%20coin%20with%20diamond%20symbol%20exchange%20digital%20currency%20icon%20on%20transparent%20background&width=56&height=56&seq=bnb1&orientation=squarish" 
            alt="" 
            className="w-14 h-14"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          
          {/* Left Content */}
          <div className="lg:w-1/2 text-center lg:text-left">
            
            {/* Logo and Brand */}
            <div className="flex items-center justify-center lg:justify-start mb-6">
              <img 
                src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
                alt="PepCoin Logo" 
                className="w-12 h-12 sm:w-14 sm:h-14 mr-3 drop-shadow-lg"
                loading="eager"
              />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                PepCoin
              </h1>
            </div>

            {/* Main Headline */}
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              The Future of
              <span className="block bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Cryptocurrency Trading
              </span>
            </h2>

            {/* Subtitle */}
            <p className="text-base sm:text-lg lg:text-xl text-blue-100 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0">
              Experience next-generation crypto trading with AI-powered insights, secure P2P transactions, and revolutionary mining technology. Join the PepCoin ecosystem today.
            </p>

            {/* Feature Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-blue-400/30 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mb-3 mx-auto lg:mx-0">
                  <i className="ri-robot-line text-white text-xl"></i>
                </div>
                <h3 className="font-semibold text-white mb-2 text-sm">AI Trading</h3>
                <p className="text-xs text-blue-200">Smart algorithms for optimal trading decisions</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-blue-400/30 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center mb-3 mx-auto lg:mx-0">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <h3 className="font-semibold text-white mb-2 text-sm">Secure P2P</h3>
                <p className="text-xs text-blue-200">Safe peer-to-peer cryptocurrency exchange</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-blue-400/30 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-3 mx-auto lg:mx-0">
                  <i className="ri-cpu-line text-white text-xl"></i>
                </div>
                <h3 className="font-semibold text-white mb-2 text-sm">Smart Mining</h3>
                <p className="text-xs text-blue-200">Revolutionary mining with automated rewards</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Link 
                to="/auth/signup" 
                className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold text-base hover:from-yellow-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-300 shadow-xl whitespace-nowrap text-center cursor-pointer"
              >
                Start Trading Now
              </Link>
              <Link 
                to="/whitepaper" 
                className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold text-base border-2 border-white/30 hover:bg-white/30 transform hover:scale-105 transition-all duration-300 shadow-lg whitespace-nowrap text-center cursor-pointer"
              >
                Read Whitepaper
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-blue-400/30">
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-yellow-400">500K+</div>
                <div className="text-sm text-blue-200">Active Traders</div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-orange-400">$2.5B+</div>
                <div className="text-sm text-blue-200">Trading Volume</div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-green-400">99.9%</div>
                <div className="text-sm text-blue-200">Uptime</div>
              </div>
            </div>
          </div>

          {/* Right Content - Interactive Dashboard Preview */}
          <div className="lg:w-1/2 w-full max-w-lg mx-auto lg:mx-0">
            <div className="relative">
              
              {/* Main Dashboard Card */}
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 shadow-2xl border border-white/20">
                
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg sm:text-xl font-bold text-white">Trading Dashboard</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-blue-200">Live</span>
                  </div>
                </div>

                {/* Portfolio Value */}
                <div className="mb-6">
                  <div className="text-sm text-blue-200 mb-1">Portfolio Value</div>
                  <div className="text-3xl font-bold text-white">$24,567.89</div>
                  <div className="text-green-400 text-sm font-medium">+12.5% (24h)</div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-blue-200">PepCoin</div>
                        <div className="font-bold text-white">1,250 PEP</div>
                      </div>
                      <img 
                        src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
                        alt="PepCoin" 
                        className="w-8 h-8"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-blue-200">Bitcoin</div>
                        <div className="font-bold text-white">0.45 BTC</div>
                      </div>
                      <img 
                        src="https://readdy.ai/api/search-image?query=Bitcoin%20cryptocurrency%20logo%20orange%20circular%20coin%20with%20B%20symbol%20digital%20currency%20icon%20professional&width=32&height=32&seq=btc2&orientation=squarish" 
                        alt="Bitcoin" 
                        className="w-8 h-8"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Link 
                    to="/pepcoin/buy-sell"
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all duration-300 whitespace-nowrap text-sm text-center cursor-pointer"
                  >
                    <i className="ri-arrow-up-line mr-2"></i>
                    Buy Crypto
                  </Link>
                  <Link 
                    to="/p2p"
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-300 whitespace-nowrap text-sm text-center cursor-pointer"
                  >
                    <i className="ri-exchange-line mr-2"></i>
                    Trade P2P
                  </Link>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg animate-bounce">
                <i className="ri-trophy-line text-xl"></i>
              </div>

              <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white shadow-lg animate-pulse">
                <i className="ri-rocket-line"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-20 fill-white">
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"></path>
          <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"></path>
          <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
        </svg>
      </div>
    </section>
  );
});

Hero.displayName = 'Hero';

export default Hero;
